import React, { ChangeEvent, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Box, Card, Chip, Stack, Typography, useTheme } from '@mui/material';
import { AxiosError } from 'axios';
import { useDebounceValue } from 'usehooks-ts';

import {
  getGetTalentProcessesQueryKey,
  ProcessQueryDTO,
  ProcessResponseDTO,
  ProcessResponseDTOAssessmentType,
  ProcessResponseDTOStatus,
  ProcessTalentResponseDTO,
  TalentDTO,
  TalentDTOType,
  useAddTalentToProcesses,
  useFilterProcesses,
  useGetTalentById,
  useGetTalentProcesses,
  useRemoveTalentsFromProcess
} from '@/api/generated';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import EmptyState from '@/components/EmptyState';
import CheckboxToggle, { StyledCheckbox } from '@/components/fields/CheckboxToggle';
import SearchInput from '@/components/fields/SearchInput';
import Loader from '@/components/Loader/Loader';
import LoadingButton from '@/components/LoadingButton';
import ProcessStatusIcon from '@/components/ProcessStatusIcon';
import { useProcessPermissions } from '@/hooks/permissions/useProcessPermissions';
import { useTalentPermissions } from '@/hooks/permissions/useTalentPermissions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { borders } from '@/theme/constants';
import { isProcessBusy } from '@/utils/process';
import { getUserFullName } from '@/utils/user';
import { TalentCreateFormValues } from '@/views/TalentsCreate/types';

const PROCESSES_STATUSES_FILTER = Object.values(ProcessResponseDTOStatus).filter(
  status => status !== ProcessResponseDTOStatus.CANCELLED && status !== ProcessResponseDTOStatus.COMPLETED
);

type AddToProcessesProps = {
  talentId?: string;
  draftTalent?: { type: TalentDTOType };
  organizationSlug?: string;
  outerFormContext?: UseFormReturn<TalentCreateFormValues>;
  footer?: ReactNode;
  onSubmit?(): void;
};

const talentTypeToProcessTypeMap = {
  [TalentDTOType.CANDIDATE]: ProcessResponseDTOAssessmentType.EXTERNAL,
  [TalentDTOType.EMPLOYEE]: ProcessResponseDTOAssessmentType.INTERNAL
};

const AddToProcesses = ({
  talentId,
  draftTalent,
  organizationSlug,
  outerFormContext,
  footer,
  onSubmit
}: AddToProcessesProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['talents', 'processes']);
  const { spacing, typography, palette } = useTheme();
  const { showMessage } = useSnackbar();
  const formMethods = useForm<TalentCreateFormValues>();
  const { setValue, watch, resetField } = outerFormContext ?? formMethods;
  const isTabLoaded = useRef(false);
  const { data: fetchedTalent } = useGetTalentById<TalentDTO>(talentId!, {
    query: {
      enabled: !!talentId
    }
  });
  const organizationId = fetchedTalent?.organization?.slug || organizationSlug;
  const { data: talentProcessesResponse } = useGetTalentProcesses(talentId!, {
    query: {
      enabled: !!talentId,
      suspense: false,
      onSuccess() {
        if (isTabLoaded.current) return;
        resetField(`selectedProcesses`);
        isTabLoaded.current = true;
      }
    }
  });

  const talentProcessesMap = useMemo(
    () => new Map(talentProcessesResponse?.processes?.map(process => [process.id!, process]) ?? []),
    [talentProcessesResponse]
  );
  const { mutateAsync: addTalentToProcesses, isLoading: isAddingToProcesses } = useAddTalentToProcesses();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounceValue(search, 400);
  const talent = draftTalent ?? fetchedTalent;
  const talentAssessmentType = talent ? [talentTypeToProcessTypeMap[talent.type!]] : [];
  const query: ProcessQueryDTO = {
    process: {
      organization: organizationId,
      status: PROCESSES_STATUSES_FILTER,
      search: debouncedSearch,
      assessmentType: [ProcessResponseDTOAssessmentType.ANY, ...talentAssessmentType]
    }
  };
  const {
    data: processesList = [],
    isFetched: isProcessesFetched,
    isLoading: isLoadingProcesses
  } = useFilterProcesses(query, {
    query: {
      suspense: false,
      enabled: !!organizationId
    }
  });
  const selectedProcesses = watch('selectedProcesses');

  const handleSearchChange = useCallback(
    (search: string) => {
      setSearch(search);

      resetField(`selectedProcesses`);
    },
    [resetField]
  );

  const isEveryChecked =
    selectedProcesses && selectedProcesses.size > 0 && processesList.length === selectedProcesses.size;
  const isSomeChecked = !!selectedProcesses?.size;

  const handleCheckAll = useCallback(() => {
    if ((isSomeChecked && !isEveryChecked) || isEveryChecked) {
      resetField(`selectedProcesses`);

      return;
    }

    const newSelectedProcesses = new Map();
    processesList.forEach(process => {
      if (talentProcessesMap.has(process.id!)) return;

      newSelectedProcesses.set(process.id!, process);
    });

    setValue('selectedProcesses', newSelectedProcesses, { shouldDirty: true });
  }, [isEveryChecked, isSomeChecked, processesList, resetField, setValue, talentProcessesMap]);

  const handleAddToProcesses = useCallback(async () => {
    if (!selectedProcesses?.size) {
      showMessage(t('common:error.somethingWentWrong'));

      return;
    }

    try {
      const processIds = Array.from(selectedProcesses.values()).map(({ id }) => id!);
      await addTalentToProcesses({
        id: talentId!,
        data: { processIds }
      });
      queryClient.invalidateQueries(getGetTalentProcessesQueryKey(talentId!));
      resetField(`selectedProcesses`);
    } catch {
      showMessage(t('common:error.somethingWentWrong'));
    }
  }, [selectedProcesses, showMessage, t, addTalentToProcesses, talentId, queryClient, resetField]);

  return (
    <FormProvider {...formMethods}>
      <Box
        component="form"
        onSubmit={onSubmit ?? formMethods.handleSubmit(handleAddToProcesses)}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Stack direction="row" alignItems="center">
          <Typography component="h2" variant="body1Bold" sx={{ padding: 3 }}>
            {t('layouts.talentEditModal.addToProcess.title')}
          </Typography>
          <SearchInput
            value={search}
            placeholder={t('processes:layouts.processes.details.info.search')}
            onChange={handleSearchChange}
          />
        </Stack>
        {isProcessesFetched && processesList.length > 1 && (
          <Stack direction="row" sx={{ mx: spacing(3), paddingInlineStart: spacing(2) }}>
            <CheckboxToggle
              value={isEveryChecked}
              onChange={handleCheckAll}
              label=""
              indeterminate={isSomeChecked && !isEveryChecked}
            />
            {talentId && (
              <LoadingButton
                variant="text"
                disabled={!isSomeChecked}
                isLoading={isAddingToProcesses}
                onClick={handleAddToProcesses}
              >
                {t('layouts.talentEditModal.addToProcess.addToProcesses')}
              </LoadingButton>
            )}
          </Stack>
        )}
        <Box
          sx={{
            flexBasis: 0,
            flexGrow: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: spacing(2, 1.5),
            px: spacing(3),
            paddingTop: spacing(3),
            paddingBottom: spacing(3)
          }}
        >
          {isProcessesFetched && processesList.length < 1 && (
            <EmptyState imageUrl="/images/empty-state-illustration.png" imageAlt={t('layouts.talent.talentEmptyAlt')}>
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', fontWeight: typography.fontWeightMedium, color: palette.grey[700] }}
              >
                {t('layouts.talentEditModal.addToProcess.noProcesses')}
              </Typography>
            </EmptyState>
          )}
          {isLoadingProcesses && !isProcessesFetched && <Loader sx={{ mx: 'auto' }} />}
          {isProcessesFetched &&
            processesList.map(process => (
              <ProcessCardSmall
                key={process.id}
                process={process}
                talent={fetchedTalent}
                talentProcesses={talentProcessesMap}
                formContext={outerFormContext ?? formMethods}
              />
            ))}
        </Box>
        {footer}
      </Box>
    </FormProvider>
  );
};

AddToProcesses.displayName = 'AddToProcesses';

export default AddToProcesses;

const ProcessCardSmall = ({
  process,
  talentProcesses,
  talent,
  formContext
}: {
  talent: TalentDTO | undefined;
  process: ProcessResponseDTO;
  talentProcesses: Map<string, ProcessTalentResponseDTO>;
  formContext: UseFormReturn<TalentCreateFormValues>;
}) => {
  const { spacing, typography } = useTheme();
  const { t } = useTranslation(['talents', 'processes']);
  const { mutateAsync: addTalentToProcesses, isLoading: isAddingToProcess } = useAddTalentToProcesses();
  const { mutate: removeTalentFromProcess, isLoading: isRemovingFromProcess } =
    useRemoveTalentsFromProcess<AxiosError>();
  const { watch, setValue, resetField } = formContext;
  const selectedProcesses = watch('selectedProcesses');
  const checked = selectedProcesses?.has(process.id!);
  const isTalentInProcess = talentProcesses.has(process.id!);
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();
  const [showConfirmRemoveFromProcess, setShowConfirmRemoveFromProcess] = useState(false);
  const talentId = talent?.id;

  const { value: talentPermissions } = useTalentPermissions({
    variables: { talentId }
  });

  const { value: processPermissions } = useProcessPermissions({
    variables: { processId: process.id! }
  });

  const canUpdateTalent = !talentId || talentPermissions?.canUpdate;
  const canAddTalents = processPermissions?.canAddTalents && canUpdateTalent;
  const canRemoveTalents = processPermissions?.canRemoveTalents && canUpdateTalent;

  const setChecked = useCallback(
    (checked: boolean) => {
      const newSelectedProcesses = new Map(selectedProcesses?.entries());

      if (checked) newSelectedProcesses.set(process.id!, process);
      else newSelectedProcesses.delete(process.id!);

      setValue('selectedProcesses', newSelectedProcesses);
    },
    [process, selectedProcesses, setValue]
  );

  const handleCheckOnChange = useCallback(
    async (_e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setChecked(checked);
    },
    [setChecked]
  );

  const handleAddToProcess = useCallback(async () => {
    if (!checked && !talent) {
      setChecked(true);

      return;
    }

    if (!talent) {
      showMessage(t('common:error.somethingWentWrong'));

      return;
    }

    try {
      await addTalentToProcesses({ id: talent.id!, data: { processIds: [process.id!] } });
      queryClient.invalidateQueries(getGetTalentProcessesQueryKey(talent.id!));
      resetField(`selectedProcesses`);
    } catch {
      showMessage(t('common:error.somethingWentWrong'));
    }
  }, [addTalentToProcesses, checked, process.id, queryClient, resetField, setChecked, showMessage, t, talent]);

  const handleRemoveFromProcess = useCallback(() => {
    if (checked && !talent) {
      setChecked(false);
      setShowConfirmRemoveFromProcess(false);

      return;
    }

    if (!talent) {
      showMessage(t('common:error.somethingWentWrong'));

      return;
    }

    removeTalentFromProcess(
      { id: process.id!, data: { talentIds: [talent.id!] } },
      {
        onSuccess: () => {
          setShowConfirmRemoveFromProcess(false);
          queryClient.invalidateQueries(getGetTalentProcessesQueryKey(talent.id!));
        },
        onError: error => {
          if (error.response?.status === 403) return showMessage(t('layouts.process.menu.removeFromProcess.forbidden'));

          return showMessage(t('error.somethingWentWrong', { ns: 'common' }));
        }
      }
    );
  }, [checked, talent, removeTalentFromProcess, process.id, setChecked, showMessage, t, queryClient]);

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: spacing(2),
        bgcolor: 'background.default',
        border: borders.default,
        borderColor: 'grey.400',
        overflow: 'visible'
      }}
      elevation={0}
    >
      {canAddTalents && (
        <StyledCheckbox
          checked={!!checked}
          value={process}
          onChange={handleCheckOnChange}
          sx={{ marginInlineEnd: spacing(1), visibility: isTalentInProcess ? 'hidden' : 'visible' }}
        />
      )}
      <ProcessStatusIcon status={process.status} />
      <Typography
        variant="body1"
        sx={{
          marginInlineStart: spacing(0.5),
          fontWeight: typography.fontWeightMedium,
          lineHeight: spacing(3)
        }}
      >
        {process.name}
      </Typography>
      {process.assessmentType && (
        <Chip
          variant="tag"
          label={t(`processes:layouts.processes.details.info.processTypes.${process.assessmentType?.toLowerCase()}`)}
          sx={{ marginInlineStart: spacing(1) }}
        />
      )}
      {isTalentInProcess || checked ? (
        <>
          {canRemoveTalents && (
            <LoadingButton
              variant="text"
              color="primary"
              disabled={talentProcesses.has(process.id!) && isProcessBusy(talentProcesses.get(process.id!)!)}
              onClick={() => setShowConfirmRemoveFromProcess(true)}
              isLoading={isRemovingFromProcess}
              sx={{ minWidth: 'auto', marginInlineStart: 'auto' }}
            >
              {t('talents:layouts.talentEditModal.addToProcess.remove')}
            </LoadingButton>
          )}
          <ConfirmationModal
            open={showConfirmRemoveFromProcess}
            title={t('processes:layouts.process.menu.removeFromProcess.confirmation', {
              talentName: getUserFullName(talent!),
              processName: process.name
            })}
            confirmButtonLabel={t('processes:layouts.process.menu.removeFromProcess.confirm')}
            discardButtonLabel={t('processes:layouts.process.menu.removeFromProcess.cancel')}
            loadingLabel={t('processes:layouts.process.menu.removeFromProcess.removing')}
            illustration={<Box component="img" src="/images/folder-green-with-circle-check.svg" />}
            onConfirm={handleRemoveFromProcess}
            onDiscard={() => setShowConfirmRemoveFromProcess(false)}
            isLoading={isRemovingFromProcess}
          />
        </>
      ) : (
        canAddTalents && (
          <LoadingButton
            variant="text"
            color="primary"
            isLoading={isAddingToProcess}
            onClick={handleAddToProcess}
            sx={{ minWidth: 'auto', marginInlineStart: 'auto' }}
          >
            {t('talents:layouts.talentEditModal.addToProcess.add')}
          </LoadingButton>
        )
      )}
    </Card>
  );
};
